import type { CSSProperties, ReactSVGElement, RefObject } from "react";
import React, { useEffect, useId, useState } from "react";

import type { IUniBoxMain } from "../types";

type UseMaskArgs = {
    mask: IUniBoxMain["mask"];
    containerRef: RefObject<HTMLDivElement | null>;
};

type UseMaskResult = {
    svgMaskStyle: CSSProperties;
    svgMaskElement: ReactSVGElement | null;
};

const svgMaskElementStyles: CSSProperties = {
    position: "fixed",
    top: "0",
    left: "-999999px",
};

export const useMask = ({ mask, containerRef }: UseMaskArgs): UseMaskResult => {
    const svgMaskId = useId();
    const [scaleX, setScaleX] = useState<number>(1);
    const [scaleY, setScaleY] = useState<number>(1);

    useEffect(() => {
        if (!mask || containerRef.current === null) {
            return;
        }

        const { height: containerHeight, width: containerWidth } =
            containerRef.current.getBoundingClientRect();

        if (React.isValidElement(mask) && mask.type === "svg") {
            const viewBox = mask.props.viewBox || "0 0 1 1";
            const [, , w, h] = viewBox.split(" ").map(parseFloat);
            const scaleX = containerWidth / (w || 1);
            const scaleY = containerHeight / (h || 1);
            setScaleX(scaleX);
            setScaleY(scaleY);
        }
    }, [containerRef, mask]);

    if (React.isValidElement(mask) && mask.type === "svg") {
        const svgMaskElements = mask.props.children;

        if (!svgMaskElements) {
            return {
                svgMaskElement: null,
                svgMaskStyle: {},
            };
        }
        const viewBox = mask.props.viewBox || "0 0 1 1";

        const finalSvgMaskElement = (
            <svg
                viewBox={viewBox}
                style={svgMaskElementStyles}
                aria-hidden
                focusable="false"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <clipPath
                        id={svgMaskId}
                        clipPathUnits="userSpaceOnUse"
                        style={{
                            transform: `scale(${scaleX}, ${scaleY})`,
                        }}
                    >
                        {svgMaskElements}
                    </clipPath>
                </defs>
            </svg>
        ) as ReactSVGElement;

        return {
            svgMaskElement: finalSvgMaskElement,
            svgMaskStyle: {
                clipPath: `url(#${svgMaskId})`,
                WebkitClipPath: `url(#${svgMaskId})`,
            },
        };
    }

    return {
        svgMaskStyle: {},
        svgMaskElement: null,
    };
};
